import {Component, HostListener, OnInit} from '@angular/core';
import {SelectItem} from "primeng/api";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {AuthGuard} from "../../../../common/auth-guard.service";
import {CommonMessagesService} from "../../../../services/common-messages.service";
import {ConversorsService} from "../../../../services/conversors.service";
import {DefaultMeasurementTypesService} from "../../../../services/default-measurement-types.service";
import {DefaultMeasurementType} from "../../../../model/default-measurement-type";
import {DataType} from "../../../../model/data-type";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {
  DefaultMeasurementTypePersistRequest
} from "../../../../model/requests/default-measurement-type-persist-request";

@Component({
  selector: 'app-default-measurement-types-form',
  templateUrl: './default-measurement-types-form.component.html',
  styleUrls: ['./default-measurement-types-form.component.scss']
})
export class DefaultMeasurementTypesFormComponent implements OnInit {

  public type: DefaultMeasurementType;

  public dataTypes: SelectItem[];

  public typeForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { oldSensor: string, oldUnit: string };

  public units: any[];
  public filteredUnits: any[];
  public convert = this.conversorsService.convert;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private defaultMeasurementTypesService: DefaultMeasurementTypesService,
              private commonMessagesService: CommonMessagesService,
              private conversorsService: ConversorsService) {
    this.dataTypes = [
      {label: 'forms.sensorType.STRING', value: DataType.STRING},
      {label: 'forms.sensorType.NUMBER', value: DataType.NUMBER},
      {label: 'forms.sensorType.BOOLEAN', value: DataType.BOOLEAN},
      {label: 'forms.sensorType.COORDINATES', value: DataType.COORDINATES},
      {label: 'forms.sensorType.ANALOG_SIGNAL', value: DataType.ANALOG_SIGNAL},
      {label: 'forms.sensorType.DIGITAL_SIGNAL', value: DataType.DIGITAL_SIGNAL}
    ];
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[1].path === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[1].path, urlSegment[2].path);
      }
    });
  }

  loadCreationForm(): void {
    this.type = new DefaultMeasurementType();
    this.createForm(this.type);
  }

  loadEditionForm(sensor: string, unit: string): void {
    this.defaultMeasurementTypesService.getDefaultMeasurementType(sensor, unit).toPromise().then(response => {
      this.type = response.defaultMeasurementType;
      this.editInfo = {oldSensor: this.type.sensor, oldUnit: this.type.unit};
      this.createForm(this.type);
    });
  }

  private createForm(type: DefaultMeasurementType): void {
    this.typeForm = this.formBuilder.group({
      sensor: [type.sensor, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      unit: [type.unit, [Validators.required, Validators.maxLength(255)]],
      dataType: [type.dataType, [Validators.required, Validators.maxLength(255)]]
    });
    this.typeForm.markAsPending();
  }

  persistTypeData(): void {
    if (!this.typeForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.defaultMeasurementTypesService.createDefaultMeasurementType(persistData).toPromise();
    } else {
      promise = this.defaultMeasurementTypesService.editDefaultMeasurementType(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/hw-catalog`], {state: {showView: 'measurement-types'}});
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.sensor + ' - ' +  persistData.unit);
      this.persistingChanges = false;
    });
  }

  public cancel(): void {
    this.router.navigate([`/system/hw-catalog`], {state: {showView: 'measurement-types'}});
  }

  private buildPersistData(): DefaultMeasurementTypePersistRequest {
    const formValue = this.typeForm.getRawValue();
    return {
      sensor: formValue.sensor,
      unit: formValue.unit,
      dataType: formValue.dataType,
      oldSensor: this.mode === 'edit' ? this.editInfo.oldSensor : null,
      oldUnit: this.mode === 'edit' ? this.editInfo.oldUnit : null
    };
  }

  filterUnit(event): void {
    const units = this.convert().possibilities();
    let filtered: any[] = [];
    let query = event.query;
    for (let i = 0; i < units.length; i++) {
      let unit = units[i];
      if (unit.toLowerCase().indexOf(query.toLowerCase()) == 0) {
        filtered.push(unit);
      }
    }
    this.filteredUnits = filtered;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.typeForm.untouched;
  }

}
